/**
 * Loading the next file. The importer decides what it recognises; this only hands
 * it the file and the portal it came from, and then states what came out.
 *
 * De kop van het scherm staat in dezelfde regel als de knop, maar het paneel en
 * de uitkomst staan eronder over de volle breedte: zo zweeft de uitkomstregel
 * niet meer over 'Datasets' heen.
 */
import { formatAantal } from '@mamimap/shared';
import { Upload, X } from 'lucide-react';
import { type ReactNode, useRef, useState } from 'react';
import { Melding } from '@/components/Melding';
import { useImporteren } from '@/lib/api/queries';
import type { ImportResultaat } from '@/lib/api/schemas';

export function Importeren({ kop }: { kop: ReactNode }) {
  const [open, zetOpen] = useState(false);
  const [bestand, zetBestand] = useState<File | null>(null);
  const [bron, zetBron] = useState('funda');
  const invoer = useRef<HTMLInputElement>(null);
  const importeren = useImporteren();

  function start() {
    if (!bestand) return;
    importeren.mutate(
      { bestand, bron: bron.trim() || 'onbekend' },
      {
        onSuccess: () => {
          zetBestand(null);
          if (invoer.current) invoer.current.value = '';
        },
      },
    );
  }

  function sluit() {
    zetOpen(false);
    zetBestand(null);
    importeren.reset();
    if (invoer.current) invoer.current.value = '';
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-4">
        {kop}
        <button
          type="button"
          className="knop"
          aria-expanded={open}
          onClick={() => (open ? sluit() : zetOpen(true))}
        >
          <Upload className="h-3.5 w-3.5" aria-hidden="true" />
          Bestand importeren
        </button>
      </div>

      {open ? (
        /* Beide groepen dragen hun naam bóven het veld, net als het inlogscherm
           en de filterbalk; de rij lijnt daarom onderlangs uit, zodat knoppen en
           veld op dezelfde regel staan. */
        <div className="paneel flex flex-wrap items-end gap-2 p-2.5">
          {/* De bestandskiezer van de browser draagt zijn eigen knop; die valt uit
              de toon. Het invoerveld blijft, maar onzichtbaar, met een eigen knop
              ervoor en de naam van het gekozen bestand ernaast. */}
          <input
            ref={invoer}
            type="file"
            accept=".csv,.tsv,.txt,.xlsx,.xlsm"
            onChange={(event) => zetBestand(event.target.files?.[0] ?? null)}
            aria-label="Bestand"
            className="sr-only"
          />
          <div>
            <span className="veldnaam">Bestand</span>
            <div className="flex items-center gap-2">
              <button type="button" className="knop-stil" onClick={() => invoer.current?.click()}>
                {bestand ? 'Ander bestand' : 'Bestand kiezen'}
              </button>
              <span
                className="max-w-56 truncate text-[12px] text-ink-soft"
                title={bestand?.name ?? undefined}
              >
                {bestand ? bestand.name : <span className="text-muted">Geen bestand gekozen</span>}
              </span>
            </div>
          </div>
          <div>
            <label className="veldnaam" htmlFor="import-bron">
              Bron
            </label>
            <input
              id="import-bron"
              className="veld w-24"
              value={bron}
              onChange={(event) => zetBron(event.target.value)}
            />
          </div>
          <button
            type="button"
            className="knop"
            onClick={start}
            disabled={!bestand || importeren.isPending}
          >
            {importeren.isPending ? 'Bezig' : 'Importeren'}
          </button>
          {/* Een paneel dat opengaat hoort ook dicht te kunnen zonder dat je de
              knop erboven terugvindt. */}
          <button
            type="button"
            onClick={sluit}
            aria-label="Importeren sluiten"
            className="ml-auto self-start text-faint hover:text-ink"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
      ) : null}

      {importeren.error ? <Melding fout={importeren.error} /> : null}
      {importeren.data ? (
        <Uitkomst resultaat={importeren.data} sluit={() => importeren.reset()} />
      ) : null}
    </div>
  );
}

function Uitkomst({ resultaat, sluit }: { resultaat: ImportResultaat; sluit: () => void }) {
  const cijfers: [string, number][] = [
    ['Rijen', resultaat.aantalRijen],
    ['Geschreven', resultaat.aantalGeschreven],
    ['Gegeocodeerd', resultaat.dataset.aantalGegeocodeerd],
    ['Met label', resultaat.dataset.aantalMetLabel],
    ['Dubbel', resultaat.aantalDubbel],
    ['Overgeslagen', resultaat.aantalOvergeslagen],
  ];

  return (
    <div className="paneel px-3 py-2">
      <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1">
        <span className="text-[12px] text-muted">{resultaat.dataset.bestandsnaam}</span>
        {cijfers.map(([naam, aantal]) => (
          <span key={naam} className="flex items-baseline gap-1.5 text-[12px]">
            <span className="text-muted">{naam}</span>
            <span className="cijfers text-ink">{formatAantal(aantal)}</span>
          </span>
        ))}
        <button
          type="button"
          onClick={sluit}
          aria-label="Uitkomst sluiten"
          className="ml-auto text-faint hover:text-ink"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>
      {resultaat.nietHerkendeKolommen.length > 0 ? (
        <p className="mt-1.5 text-[11.5px] text-muted">
          Niet herkend: {resultaat.nietHerkendeKolommen.join(', ')}
        </p>
      ) : null}
    </div>
  );
}
